/**
 * Extracted metric/dimension add/remove logic for use across UDE panel components.
 * These functions mirror the logic from UdePanelMetrics and UdePanelDimensions
 * to allow metric/dimension manipulation from the stepped workflow panels.
 */
import { getMeasurementMetric, getMeasurementSync } from '@kentik/ui-shared/query/measurements';
import { getDefaultRollupFn, getWindowFn } from '@kentik/ui-shared/query/query';
import { Measurement } from '@kentik/ui-shared/query/query.types';
import { UdeFormProps } from './form.types';
import { DefaultQuery, getDefaultQuery } from './defaultQueries';

type UdeForm = UdeFormProps['form'];

/**
 * Add a metric to the form, including rollup and window.fn configuration.
 * Mirrors UdePanelMetrics.handleAddMetric logic.
 */
export function addMetricToForm(form: UdeForm, metricValue: string, measurements: Measurement[]): void {
  const currentMetrics = form.getValue('config.data.metrics') || [];
  const measurement = form.getValue('config.data.measurement');

  if (currentMetrics.find((metric) => metric.name === metricValue)) {
    return;
  }

  form.setValue('config.data.metrics', [...currentMetrics, { name: metricValue }]);

  if (measurement) {
    const measurementData = getMeasurementSync(measurement, measurements);
    if (measurementData) {
      const metricDetails = getMeasurementMetric(measurementData, metricValue);
      if (metricDetails && !metricDetails.Aggregate) {
        const currentRollups = form.getValue('config.data.rollups') || [];
        if (!currentRollups.find((rollup) => rollup.metric === metricValue)) {
          const defaultFn = getDefaultRollupFn(metricDetails);
          form.setValue('config.data.rollups', [...currentRollups, { metric: metricValue, fn: defaultFn }]);

          const currentWindowFn = form.getValue('config.data.window.fn') || {};
          form.setValue('config.data.window.fn', {
            ...currentWindowFn,
            [metricValue]: getWindowFn(metricDetails, defaultFn)
          });
        }
      }
    }
  }
}

/**
 * Remove a metric from the form, cleaning up rollups, window.fn, sort, and filters.
 * Mirrors UdePanelMetrics.handleRemoveMetric logic.
 */
export function removeMetricFromForm(form: UdeForm, metricName: string): void {
  const currentMetrics = form.getValue('config.data.metrics') || [];
  form.setValue(
    'config.data.metrics',
    currentMetrics.filter((metric) => metric.name !== metricName)
  );

  const currentRollups = form.getValue('config.data.rollups') || [];
  form.setValue(
    'config.data.rollups',
    currentRollups.filter((rollup) => rollup.metric !== metricName)
  );

  // Clean up window.fn
  const currentWindowFn = form.getValue('config.data.window.fn') || {};
  const { [metricName]: _, ...updatedWindowFn } = currentWindowFn;
  form.setValue('config.data.window.fn', updatedWindowFn);

  // Clean up sort
  const currentSort = form.getValue('config.data.sort') || [];
  form.setValue(
    'config.data.sort',
    currentSort.filter(
      (s) =>
        // Rollup format is fn_metricName — but metric names can contain underscores,
        // so match against the known metric name at the end of the rollup string
        !s.rollup.endsWith(`_${metricName}`) && s.rollup !== metricName
    )
  );
}

/**
 * Add a dimension to the form.
 * Mirrors UdePanelDimensions.handleAddDimension logic.
 */
export function addDimensionToForm(form: UdeForm, dimensionName: string): void {
  const currentDimensions = form.getValue('config.data.dimensions') || [];
  const newDimName = typeof dimensionName === 'string' ? dimensionName : (dimensionName as { key: string }).key;

  if (!currentDimensions.find((dimension) => dimension.name === newDimName)) {
    form.setValue('config.data.dimensions', [...currentDimensions, { name: newDimName }]);
  }
}

/**
 * Remove a dimension from the form.
 * Mirrors UdePanelDimensions.handleRemoveDimension logic.
 */
export function removeDimensionFromForm(form: UdeForm, dimensionName: string): void {
  const currentDimensions = form.getValue('config.data.dimensions') || [];
  form.setValue(
    'config.data.dimensions',
    currentDimensions.filter((dimension) => dimension.name !== dimensionName)
  );
}

function applyStaticDefaultQuery(form: UdeForm, defaults: DefaultQuery, measurements: Measurement[]): void {
  for (const metricKey of defaults.metrics) {
    addMetricToForm(form, metricKey, measurements);
  }
  for (const dimensionKey of defaults.dimensions) {
    addDimensionToForm(form, dimensionKey);
  }
}

function applyNmsDefaultQuery(form: UdeForm, measurementPath: string, measurements: Measurement[]): void {
  const measurementObj = measurements.find((m) => m.measurement === measurementPath);
  const metricKeys = Object.keys(measurementObj?.storage?.Metrics ?? {});
  if (metricKeys.length === 0) {
    return;
  }

  addMetricToForm(form, metricKeys[0], measurements);

  if (measurementObj?.storage?.Dimensions?.device_name) {
    addDimensionToForm(form, 'device_name');
  }
}

/**
 * Apply default metrics and dimensions for a measurement, if defined.
 * Only adds items that are not already selected.
 * Should be called when a measurement is first set on the form.
 */
export function applyDefaultQuery(form: UdeForm, measurementPath: string, measurements: Measurement[]): void {
  const defaults = getDefaultQuery(measurementPath);
  if (defaults) {
    applyStaticDefaultQuery(form, defaults, measurements);
    return;
  }

  if (measurementPath.startsWith('/metrics/')) {
    applyNmsDefaultQuery(form, measurementPath, measurements);
  }
}
